import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import {
  QuoteDiscountLine,
  QuoteDiscount,
  isMultiCurrency,
  type CurrencyCode,
} from "@forgepoint/shared";
import type { QuoteCurrencyFields } from "@/lib/quoteCurrency";
import { cn } from "@/lib/utils";
import { GHOST_FIELD_CLASSES } from "../cells/GhostInput";
import { GhostInput } from "../cells/GhostInput";
import { CurrencyCell } from "../cells/CurrencyCell";

interface DiscountRowProps {
  line: QuoteDiscountLine;
  readOnly: boolean;
  currencyCode?: CurrencyCode;
  /** Quote currency context; AMOUNT values are entered in the display currency. */
  currencyContext?: QuoteCurrencyFields;
  onUpdate: (line: QuoteDiscountLine) => void;
  onRemove: () => void;
}

export function DiscountRow({
  line,
  readOnly,
  currencyCode,
  currencyContext,
  onUpdate,
  onRemove,
}: DiscountRowProps) {
  const handleDescriptionChange = (value: string) => {
    if (readOnly) {
      return;
    }
    onUpdate({ ...line, description: value });
  };

  const handleDiscountTypeChange = (type: "PERCENTAGE" | "AMOUNT") => {
    if (readOnly) {
      return;
    }
    const newDiscount: QuoteDiscount =
      type === "PERCENTAGE"
        ? { type: "PERCENTAGE", percentage: "10" }
        : { type: "AMOUNT", amount: "10.00" };
    onUpdate({ ...line, discount: newDiscount });
  };

  const handleDiscountValueChange = (value: string) => {
    if (readOnly) {
      return;
    }
    const newDiscount: QuoteDiscount =
      line.discount.type === "PERCENTAGE"
        ? { type: "PERCENTAGE", percentage: value }
        : { type: "AMOUNT", amount: value };
    onUpdate({ ...line, discount: newDiscount });
  };

  const handleAmountChange = (value: string) => {
    if (readOnly) {
      return;
    }
    onUpdate({
      ...line,
      discount: { type: "AMOUNT", amount: value },
    });
  };

  const multiCurrency = !!currencyContext && isMultiCurrency(currencyContext);
  // AMOUNT values are entered and stored in the quote's display currency.
  const amountCurrencyCode = multiCurrency
    ? (currencyContext?.quoteCurrency ?? currencyCode)
    : currencyCode;

  return (
    <TableRow className="group">
      {/* Badge + Description */}
      <TableCell className="px-1 sm:px-2 py-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <Badge variant="destructive" className="text-xs shrink-0">
            Discount
          </Badge>
          <GhostInput
            value={line.description}
            onChange={handleDescriptionChange}
            placeholder="Discount description"
            disabled={readOnly}
            className="flex-1 text-xs sm:text-sm"
          />
        </div>
      </TableCell>

      {/* Type Selector */}
      <TableCell className="px-1 sm:px-2 py-2">
        <Select
          value={line.discount.type}
          onValueChange={(v) =>
            handleDiscountTypeChange(v as "PERCENTAGE" | "AMOUNT")
          }
          disabled={readOnly}
        >
          <SelectTrigger
            className={cn(
              "w-full sm:w-32 h-8 text-xs sm:text-sm",
              GHOST_FIELD_CLASSES,
            )}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PERCENTAGE">Percentage</SelectItem>
            <SelectItem value="AMOUNT">Fixed Amount</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>

      {/* Value Input */}
      <TableCell className="text-right px-1 sm:px-2 py-2">
        <div className="flex items-center justify-end gap-1">
          {line.discount.type === "PERCENTAGE" ? (
            <>
              <GhostInput
                type="number"
                value={line.discount.percentage}
                onChange={handleDiscountValueChange}
                disabled={readOnly}
                align="right"
                className="w-14 sm:w-20 text-xs sm:text-sm"
              />
              <span className="text-xs sm:text-sm text-muted-foreground">
                %
              </span>
            </>
          ) : (
            <div className="flex w-full flex-col items-end gap-0.5">
              <div className="flex w-full items-center justify-end gap-1">
                <span className="shrink-0 text-xs sm:text-sm text-muted-foreground">
                  {!amountCurrencyCode || amountCurrencyCode === "USD"
                    ? "$"
                    : amountCurrencyCode}
                </span>
                <CurrencyCell
                  value={line.discount.amount}
                  onChange={handleAmountChange}
                  disabled={readOnly}
                  editable={!readOnly}
                  currencyCode={amountCurrencyCode}
                />
              </div>
            </div>
          )}
        </div>
      </TableCell>

      {/* Actions */}
      <TableCell className="px-1 sm:px-2 py-2">
        {!readOnly && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 sm:h-8 sm:w-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity touch-manipulation"
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
